import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import useAxios from "../../../utils/useAxios";
import { useParams } from "react-router-dom";
import SelectInput from "../../../components/SelectInput";
import ErrorMessage from "../../../components/ErrorMessage";
import { Block, gamesOptions, participantsOptions } from "../../../constants";
import useSubmitLock from "../../../hooks/useSubmitLock";
import TournamentBlockToggle from "../TournamentBlockToggle";
import PageLoader from "../../../components/PageLoader";

const bracketOptions = [
    { id: 1, name: "Placement" },
    { id: 2, name: "Single elimination" },
    { id: 3, name: "Single elimination + 3rd place match" },
];

// One flat list for the per-stage "type" select, merging the group format
// (itself split into two variants - see __placeGroup below) with each
// bracket sub-type (ids kept as strings so these and the numeric draw_type
// ids can share one SelectInput's strict id === id matching - see
// stageTypeValue/handleTypeChange below for the mapping back to
// __format/draw_type/__placeGroup).
const stageTypeOptions = [
    { id: "groups", name: "Group stage" },
    { id: "groups_place", name: "Groups - place distribution" },
    ...bracketOptions.map(o => ({ id: String(o.id), name: o.name })),
];

// __placeGroup distinguishes the two group variants client-side (regular
// qualifying round-robin vs. a round-robin that plays for specific final
// places, e.g. 5th-8th). It's a local UI flag, not sent to the backend -
// the backend-visible signal is start_place being set or null (see
// backend-tasks/2026-08-31-group-stage-place-in-results.md). Deriving the
// select's value straight from "start_place is set" instead of this flag
// would snap the dropdown back to "Group stage" the instant someone picks
// "Groups - place distribution" but hasn't typed a starting place yet.
const stageTypeValue = (stage) => {
    if (stage.__format === "groups") return stage.__placeGroup ? "groups_place" : "groups";
    if (stage.__format === "draws" && stage.draw_type != null) return String(stage.draw_type);
    return null;
};

// GroupStage.order and DrawStage.draw_number are two independent sequences
// on the backend (no shared "position among all stages" field) - see
// AI_ASSISTANT_STATUS.md-adjacent investigation. This screen presents them
// as one merged list purely client-side: on fetch, each type is sorted by
// its own field, then the two lists are merged by treating that field's
// raw number as a shared position - a reasonable reconstruction as long as
// stages were numbered sequentially when created, but not a real guarantee
// (nothing forces group order=1 and draw draw_number=1 to mean "created
// same slot"). On save, position in the on-screen array becomes the new
// order/draw_number, scoped separately per format, so the two backend
// sequences stay exactly the shape the rest of the app (TournamentGroups.js/
// TournamentGrids.js's own useFetchStages-driven tabs) already expects.
const TournamentDrawStagesSettings = ({ tournamentId, tournamentBlocks, setTournament, onSaved, onClose, onReset }) => {
    const { t } = useTranslation();
    const api = useAxios();
    const { id: routeId } = useParams();
    const id = tournamentId || routeId;
    const { isSubmitting, wrapSubmit } = useSubmitLock();

    const [initialStages, setInitialStages] = useState([]);
    const [stages, setStages] = useState([]);
    // Draft text for the "Number of stages" input while it's focused - null
    // outside of editing, when the field just displays stages.length. Kept
    // separate from `stages` itself so an in-progress edit (e.g. backspacing
    // "2" down to "" before typing "3") doesn't touch it: applying the
    // resize on every keystroke meant that momentary "" - Number("") is 0 -
    // truncated `stages` to nothing right then, destroying every already-
    // configured stage's data before the new digit was even typed.
    const [stagesCountDraft, setStagesCountDraft] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [errors, setErrors] = useState({});
    // Guards fetchStages against out-of-order responses - same reasoning as
    // TournamentGroupsSettings.js/TournanentGridsSettings.js's own ref.
    const stagesReqRef = useRef(0);

    useEffect(() => {
        fetchStages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stageKey = (stage) => stage.id ?? stage.temp_id;

    const fetchStages = async () => {
        setLoading(true);
        stagesReqRef.current += 1;
        const currentReq = stagesReqRef.current;
        try {
            const [groupsRes, drawsRes] = await Promise.all([
                api.get(`/api/v1.0/tournaments/${id}/group-stage/`),
                api.get(`/api/v1.0/tournaments/${id}/draw-stage/`),
            ]);
            if (stagesReqRef.current !== currentReq) return;

            const groupStages = (groupsRes.data.results || []).map(s => ({
                ...s, __format: "groups", __pos: s.order || 0, __placeGroup: s.start_place != null,
            }));
            const drawStages = (drawsRes.data.results || []).map(s => ({
                ...s, __format: "draws", __pos: s.draw_number || 0,
            }));

            const merged = [...groupStages, ...drawStages].sort((a, b) => a.__pos - b.__pos);
            setInitialStages(merged);
            setStages(merged);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
            setIsInitialLoading(false);
        }
    };

    // A new stage starts with no type chosen (__format: null) - the type
    // select below prompts the user to pick Groups/one of the bracket kinds
    // before any format-specific fields (or the games-in-set select) show up.
    const blankStage = () => ({ temp_id: crypto.randomUUID(), __format: null, name: "", players_quantity: null, games_in_set: null });

    const resizeStages = (count) => setStages(prev => {
        if (count === prev.length) return prev;
        if (count < prev.length) return prev.slice(0, count);
        return [...prev, ...Array.from({ length: count - prev.length }, blankStage)];
    });

    // Growing is always safe (it only appends fresh blank stages) - applied
    // live, on every keystroke, same as before this got split into a draft
    // at all. Shrinking is the destructive direction (prev.slice truncates
    // real, already-configured stages off the end) - typing a bigger target
    // digit-by-digit always passes through a smaller intermediate value on
    // the way there (typing "12" is "1", then "12" - the "1" alone would
    // truncate down to 1 stage, permanently losing 2-5's data, well before
    // the "2" that was meant to make it "12" ever gets typed), so that
    // direction only actually resizes once the user commits (blur/Enter),
    // not on every keystroke. An explicit "" also doesn't resize yet, for
    // the same reason (Number("") is 0, which reads as "shrink to zero").
    const handleStagesCountChange = (e) => {
        const raw = e.target.value;
        setStagesCountDraft(raw);
        if (raw === "") return;
        const count = Math.max(0, Math.floor(Number(raw)) || 0);
        if (count > stages.length) resizeStages(count);
    };

    // Commits the draft (blur, or Enter) - applies a shrink (or a still-
    // untouched "") that handleStagesCountChange above deliberately held off
    // on, once the user is done typing a real final number.
    const commitStagesCount = () => {
        if (stagesCountDraft === null) return;
        const count = Math.max(0, Math.floor(Number(stagesCountDraft)) || 0);
        setStagesCountDraft(null);
        resizeStages(count);
    };

    const handleTypeChange = (key) => (pickedId) => {
        setStages(prev => {
            const stage = prev.find(s => stageKey(s) === key);
            if (pickedId === "groups") {
                return prev.map(s => s === stage ? { ...s, __format: "groups", __placeGroup: false, draw_type: undefined, start_place: null } : s);
            }
            if (pickedId === "groups_place") {
                return prev.map(s => s === stage ? { ...s, __format: "groups", __placeGroup: true, draw_type: undefined } : s);
            }
            // Bracket place (draw_number) is required and has no server-side
            // default - suggest the next free one instead of leaving it
            // blank, since a blank required field here silently blocks Save
            // with no obvious visual cue.
            const nextDrawNumber = stage?.draw_number
                ?? Math.max(0, ...prev.filter(s => s.__format === "draws").map(s => s.draw_number || 0)) + 1;
            return prev.map(s => s === stage
                ? { ...s, __format: "draws", draw_type: Number(pickedId), draw_number: nextDrawNumber }
                : s);
        });
        setErrors(prev => ({ ...prev, [key]: { ...prev[key], type: undefined } }));
    };

    const removeStage = (key) => {
        setStages(prev => prev.filter(s => stageKey(s) !== key));
        setErrors(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const handleChange = (key, field) => (eOrValue) => {
        const value = eOrValue?.target ? eOrValue.target.value : eOrValue;
        setStages(prev => prev.map(s => (stageKey(s) === key ? { ...s, [field]: value } : s)));
        setErrors(prev => ({ ...prev, [key]: { ...prev[key], [field]: undefined } }));
    };

    const handleSave = wrapSubmit(async (e) => {
        e?.preventDefault();
        const newErrors = {};

        stages.forEach(stage => {
            const stageErrors = {};
            if (!stage.__format) {
                stageErrors.type = t("Stage type is required");
            } else if (stage.__format === "groups") {
                if (!stage.group_quantity) stageErrors.group_quantity = t("Groups quantity is required");
                if (!stage.players_quantity) stageErrors.players_quantity = t("Players per group is required");
                if (stage.__placeGroup && !stage.start_place) stageErrors.start_place = t("Starting place is required");
            } else {
                if (!stage.draw_type) stageErrors.draw_type = t("Bracket type is required");
                if (!stage.draw_number) stageErrors.draw_number = t("Draw number is required");
                const players = Number(stage.players_quantity);
                if (!players) stageErrors.players_quantity = t("Participants quantity is required");
                else if (players <= 1) stageErrors.players_quantity = t("Participants quantity must be greater than 1");
                else if (players % 2 !== 0) stageErrors.players_quantity = t("Participants quantity must be even");
            }
            if (Object.keys(stageErrors).length) newErrors[stageKey(stage)] = stageErrors;
        });

        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        setErrors({});
        setLoading(true);

        try {
            const removedStages = initialStages.filter(s => !stages.some(ns => ns.id === s.id));
            await Promise.all(removedStages.map(stage =>
                api.delete(`/api/v1.0/tournaments/${id}/${stage.__format === "groups" ? "group-stage" : "draw-stage"}/${stage.id}/`)
            ));

            let groupOrder = 0;

            for (const stage of stages) {
                if (stage.__format === "groups") {
                    groupOrder += 1;
                    const startPlace = stage.__placeGroup ? Number(stage.start_place) : null;
                    const payload = {
                        name: stage.name,
                        group_quantity: stage.group_quantity,
                        players_quantity: stage.players_quantity,
                        games_in_set: stage.games_in_set,
                        start_place: startPlace,
                        order: groupOrder,
                    };
                    if (!stage.id) {
                        // GroupStageListAPIView.perform_create does NOT call
                        // sync_groups() (only perform_update does) - a bare
                        // POST would leave group_quantity/players_quantity
                        // set on the stage with no actual Group rows under
                        // it. Follow up with a PATCH of the same values to
                        // trigger sync_groups() right away, instead of
                        // leaving the stage looking configured but empty
                        // until some later unrelated edit happens to PATCH it.
                        const created = await api.post(`/api/v1.0/tournaments/${id}/group-stage/`, payload);
                        await api.patch(`/api/v1.0/tournaments/${id}/group-stage/${created.data.id}/`, payload);
                    } else {
                        // GroupStageDetailsAPIView.perform_update calls
                        // sync_groups() on EVERY PATCH (unconditionally
                        // regenerating this stage's actual Group rows), so
                        // unlike draws below a plain PATCH is always enough
                        // to apply a real change here - but it must only run
                        // when something for THIS stage actually changed.
                        // Saving used to PATCH every existing stage
                        // unconditionally just because the form as a whole
                        // was submitted (e.g. only to add one more stage
                        // elsewhere in the list) - sync_groups() then
                        // silently reset every other already-configured
                        // stage's groups/results along with it.
                        const original = initialStages.find(s => s.id === stage.id);
                        if (!original || original.__format !== "groups") {
                            // Stage type was switched from a bracket to Group
                            // stage on an already-saved stage - group-stage
                            // and draw-stage are different backend resources
                            // (separate tables/endpoints), so the leftover id
                            // still belongs to the OLD draw-stage record, not
                            // a group-stage one. Reusing it as-is 404s (wrong
                            // endpoint) or reads undefined fields off the
                            // wrong-shaped `original`, throwing and aborting
                            // the whole save silently. Delete the old record
                            // via its own (draws) endpoint, then create fresh
                            // here, same as the !stage.id branch above.
                            if (original) {
                                await api.delete(`/api/v1.0/tournaments/${id}/draw-stage/${original.id}/`);
                            }
                            const created = await api.post(`/api/v1.0/tournaments/${id}/group-stage/`, payload);
                            await api.patch(`/api/v1.0/tournaments/${id}/group-stage/${created.data.id}/`, payload);
                        } else {
                            const unchanged = original.name === stage.name
                                && original.group_quantity === stage.group_quantity
                                && original.players_quantity === stage.players_quantity
                                && original.games_in_set === stage.games_in_set
                                && (original.start_place ?? null) === startPlace
                                && original.order === groupOrder;
                            if (!unchanged) {
                                await api.patch(`/api/v1.0/tournaments/${id}/group-stage/${stage.id}/`, payload);
                            }
                        }
                    }
                } else {
                    const payload = {
                        name: stage.name,
                        draw_type: stage.draw_type,
                        players_quantity: stage.players_quantity,
                        games_in_set: stage.games_in_set,
                        draw_number: stage.draw_number,
                    };

                    if (!stage.id) {
                        await api.post(`/api/v1.0/tournaments/${id}/draw-stage/`, payload);
                        continue;
                    }

                    const original = initialStages.find(s => s.id === stage.id);
                    if (!original || original.__format !== "draws") {
                        // Mirror of the groups branch above: stage type was
                        // switched from Group stage to a bracket on an
                        // already-saved stage, so the leftover id still
                        // belongs to the OLD group-stage record. Delete that
                        // via its own endpoint, then create fresh here.
                        if (original) {
                            await api.delete(`/api/v1.0/tournaments/${id}/group-stage/${original.id}/`);
                        }
                        await api.post(`/api/v1.0/tournaments/${id}/draw-stage/`, payload);
                        continue;
                    }

                    const criticalChanged =
                        original.draw_type !== stage.draw_type ||
                        original.draw_number !== stage.draw_number ||
                        original.players_quantity !== stage.players_quantity;

                    if (criticalChanged) {
                        // DrawStage.save() only auto-generates the bracket
                        // skeleton on create (is_new), not on update - a
                        // plain PATCH here would leave a stale bracket, so
                        // this mirrors TournanentGridsSettings.js's own
                        // delete+recreate for critical field changes.
                        await api.delete(`/api/v1.0/tournaments/${id}/draw-stage/${stage.id}/`);
                        await api.post(`/api/v1.0/tournaments/${id}/draw-stage/`, payload);
                    } else if (original.name !== stage.name || original.games_in_set !== stage.games_in_set) {
                        // Only the two non-critical fields left to check here
                        // (draw_type/draw_number/players_quantity already
                        // confirmed unchanged above) - skip the call
                        // entirely when even those didn't change, same
                        // reasoning as the group branch above: saving the
                        // form just to add a stage elsewhere shouldn't also
                        // resubmit every other stage that isn't actually
                        // being edited.
                        await api.patch(`/api/v1.0/tournaments/${id}/draw-stage/${stage.id}/`, payload);
                    }
                }
            }

            await fetchStages();
            onSaved?.();
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    });

    const handleReset = wrapSubmit(async (e) => {
        e?.preventDefault();
        if (!window.confirm(t("Are you sure you want to reset?"))) return;
        setLoading(true);
        try {
            await Promise.all([
                api.post(`/api/v1.0/tournaments/${id}/group-stage/reset/`),
                api.post(`/api/v1.0/tournaments/${id}/draw-stage/reset/`),
            ]);
            await fetchStages();
            onReset ? onReset() : onSaved?.();
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    });

    if (isInitialLoading) {
        return <PageLoader />;
    }

    return (
        <form style={{ position: "relative" }}>
            <div style={{ marginTop: "10px" }}>
                <label>{t("Number of stages")}</label>
                <input
                    type="number"
                    min={0}
                    value={stagesCountDraft !== null ? stagesCountDraft : (stages.length || "")}
                    onChange={handleStagesCountChange}
                    onBlur={commitStagesCount}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                />
            </div>

            {stages.map((stage, idx) => {
                const key = stageKey(stage);
                return (
                    <div key={key} style={{ marginTop: "10px" }}>
                        <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span>{t("Stage")} {idx + 1}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                {stage.__format && (
                                    <TournamentBlockToggle
                                        tournamentId={id}
                                        tournamentBlocks={tournamentBlocks}
                                        setTournament={setTournament}
                                        blockType={stage.__format === "groups" ? Block.GROUPS : Block.DRAWS}
                                    />
                                )}
                                <FeatherIcon
                                    icon="trash-2"
                                    size={16}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => removeStage(key)}
                                />
                            </div>
                        </label>

                        <div className="group-stage">
                            <div>
                                <label>{t("Name")}</label>
                                <input
                                    type="text"
                                    value={stage.name || ""}
                                    onChange={handleChange(key, "name")}
                                    placeholder={`${t("Stage")} ${idx + 1}`}
                                />
                            </div>

                            <div>
                                <SelectInput
                                    default_text={t("Stage type")}
                                    label_text={t("Stage type")}
                                    data={stageTypeOptions}
                                    default_value={stageTypeValue(stage)}
                                    setInputValue={handleTypeChange(key)}
                                />
                                {errors[key]?.type && (
                                    <ErrorMessage error={errors[key].type} />
                                )}
                            </div>

                            {stage.__format === "groups" && (
                                <>
                                    <div>
                                        <label>{t("Number of groups")}</label>
                                        <input
                                            type="number"
                                            min={1}
                                            value={stage.group_quantity || ""}
                                            onChange={e => handleChange(key, "group_quantity")(Number(e.target.value))}
                                        />
                                        {errors[key]?.group_quantity && (
                                            <ErrorMessage error={errors[key].group_quantity} />
                                        )}
                                    </div>
                                    <div>
                                        <label>{t("Number participants of group")} ({t("Default")})</label>
                                        <input
                                            type="number"
                                            min={2}
                                            value={stage.players_quantity || ""}
                                            onChange={e => handleChange(key, "players_quantity")(Number(e.target.value))}
                                        />
                                        {errors[key]?.players_quantity && (
                                            <ErrorMessage error={errors[key].players_quantity} />
                                        )}
                                    </div>
                                    {stage.__placeGroup && (
                                        <div>
                                            <label>{t("Starting place")}</label>
                                            <input
                                                type="number"
                                                min={1}
                                                value={stage.start_place ?? ""}
                                                onChange={e => handleChange(key, "start_place")(e.target.value === "" ? "" : Number(e.target.value))}
                                                placeholder={t("e.g. 1")}
                                            />
                                            {errors[key]?.start_place && (
                                                <ErrorMessage error={errors[key].start_place} />
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {stage.__format === "draws" && (
                                <div>
                                    <label>{t("Bracket place")}</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={stage.draw_number ?? ""}
                                        onChange={e => handleChange(key, "draw_number")(e.target.value === "" ? "" : Number(e.target.value))}
                                    />
                                    {errors[key]?.draw_number && (
                                        <ErrorMessage error={errors[key].draw_number} />
                                    )}
                                </div>
                            )}

                            {stage.__format === "draws" && (
                                <div>
                                    <SelectInput
                                        default_text={t("Number of participants")}
                                        label_text={t("Number of participants")}
                                        data={participantsOptions}
                                        default_value={stage.players_quantity}
                                        setInputValue={handleChange(key, "players_quantity")}
                                    />
                                    {errors[key]?.players_quantity && (
                                        <div style={{ marginTop: "-15px" }}>
                                            <ErrorMessage error={errors[key].players_quantity} />
                                        </div>
                                    )}
                                </div>
                            )}

                            {stage.__format && (
                                <div>
                                    <SelectInput
                                        default_text={t("Games in set")}
                                        label_text={t("Type score (default)")}
                                        data={gamesOptions}
                                        default_value={stage.games_in_set}
                                        setInputValue={handleChange(key, "games_in_set")}
                                    />
                                    <small style={{ opacity: 0.6 }}>
                                        {t("This is only the starting format - a 2nd/3rd set can still be added to any match by hand, regardless of this setting")}
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            <br />

            <ul className="tabs settings-form-actions">
                <li>
                    <a
                        href="#"
                        className={`action-button ${loading || isSubmitting ? "disabled" : ""}`}
                        onClick={loading || isSubmitting ? (e) => e.preventDefault() : handleSave}
                        aria-disabled={loading || isSubmitting}
                    >
                        <FeatherIcon icon="check" /> {loading ? t("Saving...") : t("Save")}
                    </a>
                </li>
                <li>
                    <a
                        href="#"
                        className={`action-button ${loading || isSubmitting ? "disabled" : ""}`}
                        onClick={loading || isSubmitting ? (e) => e.preventDefault() : handleReset}
                        aria-disabled={loading || isSubmitting}
                    >
                        <FeatherIcon icon="rotate-ccw" /> {loading ? t("Resetting...") : t("Reset")}
                    </a>
                </li>
            </ul>
        </form>
    );
};

export default TournamentDrawStagesSettings;
